import * as React from 'react';
import {FindFiltersInput} from '../../types/graphql';
import {Layout, Mode} from '../../types/ui';
import maybe from '../../lib/maybe';
import findFilters from '../../lib/findFilters';
import {
  ArtistHeroSearchInputSuggestions, GenreHeroSearchInputSuggestions,
  HeroSearchInputSuggestion,
  HeroSearchInputSuggestionsProps, MoodHeroSearchInputSuggestions, ThemeHeroSearchInputSuggestions
} from './search-input-suggestions';
import {classes, style} from 'typestyle';
import {em, important, percent, rem, viewHeight} from 'csx';
import {centerCenter, horizontal, vertical} from 'csstips';
import {fontBody} from '../../theme/font';
import {colorBackground, colorContrast, colorWhite} from '../../theme/color';
import {mediaMobileOnly, mediaTablet} from '../../theme/media';
import {SVGIcon} from '../svg-icon';
import {ButtonCircle} from '../button-circle';
import {ButtonPill} from '../button-pill';

type Props = {
  onSelect?(match: HeroSearchInputSuggestion, query: string, filters: FindFiltersInput): any;
  onMode?(mode: Mode): any;
  onLayout?(layout: Layout): any;
  onReset?(): any;
  defaultFilters?: Partial<FindFiltersInput>;
  defaultMode?: Mode;
  forceActive?: boolean;
}

type QueryResultMap = {[index: number]: string};

type State = {
  mode: Mode;
  layout: Layout;
  query: string;
  waiting: boolean;
  suggestions: boolean;
  scrolling: boolean;
  scrollValue: string;
  activeIndex: number;
  placeholderIndex: number;
  total: number;
  resultMap: QueryResultMap;
  cache: HeroSearchInputSuggestion[] | null;
}

type Match = {
  name: string;
  slug: string;
  images: {
    identity: string;
  }
}

export class HeroSearchInput extends React.Component<Props, State> {
  private $input?: HTMLInputElement;
  private _mounted: boolean = false;
  private _placeholderIterator: any;

  static defaultState: State = {
    mode: Mode.THEME,
    layout: Layout.GRID,
    suggestions: false,
    scrolling: false,
    scrollValue: '',
    query: '',
    activeIndex: -1,
    placeholderIndex: 0,
    total: 0,
    resultMap: {},
    waiting: false,
    cache: null,
  };

  constructor(props: Props) {
    super(props);
    const {defaultFilters = {}} = props;

    this.state = {
      ...HeroSearchInput.defaultState,
      ...HeroSearchInput.getStateFromFilter(defaultFilters),
      mode: props.defaultMode || HeroSearchInput.defaultState.mode,
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.defaultFilters !== this.props.defaultFilters) {
      this.setState({
        ...HeroSearchInput.defaultState,
        ...HeroSearchInput.getStateFromFilter(this.props.defaultFilters)
      })
    }

    if (prevProps.defaultMode !== this.props.defaultMode) {
      this.setState({mode: this.props.defaultMode, suggestions: true})
    }
  }

  render() {
    const {forceActive, onReset} = this.props;
    const {mode, cache, query, suggestions, placeholderIndex, scrolling, scrollValue, activeIndex} = this.state;
    const isNotDefault = scrollValue && scrolling && activeIndex !== -1;
    const placeholder = cache && cache[placeholderIndex]
      ? `e.g. ${cache[placeholderIndex].name}`
      : '';

    return (
      <div className={HeroSearchInput.styles.container}>
        <form className={HeroSearchInput.styles.inputs} onSubmit={this.submit}>
          <div onClick={onReset} className={classes(
            HeroSearchInput.styles.reset,
            onReset
              ? HeroSearchInput.styles.resetVisible
              : HeroSearchInput.styles.resetHidden
          )}>
            <SVGIcon.Refresh size={16} />
          </div>
          <input
            className={classes(
              HeroSearchInput.styles.input,
              forceActive && HeroSearchInput.styles.inputActive,
              HeroSearchInput.styles.inputQuery,
              onReset
                ? HeroSearchInput.styles.inputQueryResetVisible
                : HeroSearchInput.styles.inputQueryResetHidden
            )}
            value={isNotDefault ? scrollValue : query}
            autoComplete="off"
            placeholder={placeholder}
            ref={el => this.$input = el}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onChange={this.updateQuery}
            onKeyDown={this.handleKey}
          />
          <select
            value={mode}
            onChange={this.updateMode}
            className={classes(
              HeroSearchInput.styles.input,
              forceActive && HeroSearchInput.styles.inputActive,
              HeroSearchInput.styles.inputSelect
            )}>
            <option value={Mode.ARTIST}>Artist</option>
            <option value={Mode.GENRE}>Genre</option>
            <option value={Mode.MOOD}>Mood</option>
            <option value={Mode.THEME}>Theme</option>
          </select>
          {/*<select value={layout} onChange={this.updateLayout} className={classes(HeroSearchInput.styles.input, HeroSearchInput.styles.inputSelect)}>*/}
            {/*<option value={Layout.GRID}>Grid</option>*/}
            {/*<option value={Layout.LIST}>List</option>*/}
          {/*</select>*/}
          <SVGIcon.ArrowRight size={10} className={HeroSearchInput.styles.arrow} />
        </form>
        {this.renderMatches()}

      </div>
    )
  }

  renderMatches = () => {
    const {mode, query, activeIndex, suggestions} = this.state;

    const commonProps: HeroSearchInputSuggestionsProps = {
      query,
      activeIndex,
      onSelect: this.handleSuggestionSelect,
      onHighlight: this.handleSuggestionHighlight,
      onSuggestions: this.handleSuggestions,
      show: suggestions
    };

    switch (mode) {
      case Mode.ARTIST:
        return <ArtistHeroSearchInputSuggestions {...commonProps}/>;
      case Mode.GENRE:
        return <GenreHeroSearchInputSuggestions {...commonProps}/>;
      case Mode.MOOD:
        return <MoodHeroSearchInputSuggestions {...commonProps}/>;
      case Mode.THEME:
        return <ThemeHeroSearchInputSuggestions {...commonProps}/>;
      default:
        return null;
    }
  };

  submit = (event?: React.FormEvent<any>) => {
    if (event) event.preventDefault();

    const {query, cache} = this.state;
    const match = cache && cache.find(k => k.name === query)
      || {name: query, slug: query, images: {identity: ''}};

    this.selectFromMatch(match);
    return this.blurInput();
  };

  handleKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!this._mounted)
      return;

    switch (event.key) {
      case 'ArrowUp':
        event.preventDefault();
        return this.setState({
          suggestions: true,
          scrolling: true,
          activeIndex: Math.max(this.state.activeIndex - 1, -1)
        });

      case 'ArrowDown':
        event.preventDefault();
        return this.setState({
          suggestions: true,
          scrolling: true,
          activeIndex: this.state.activeIndex + 1
        });

      case 'Enter':
        event.preventDefault();
        if (!this.state.suggestions || this.state.activeIndex === -1) {
          return this.submit();
        }

        // otherwise select the currently highlighted choice and close
        const match = this.getMatchByIndex(this.state.activeIndex);
        return match && this.selectFromMatch(match);

      default:
        return this.setState({
          activeIndex: -1
        })
    }
  };

  selectFromMatch = (match: Match) => {
    return this.setState({
      scrolling: false,
      scrollValue: '',
      suggestions: false,
      query: match.name,
      activeIndex: -1,
    }, this.notifyParentSelect.bind(this, match));
  };

  notifyParentSelect = (match: Match) => {
    const {onSelect} = this.props;
    const {query, mode} = this.state;

    if (!onSelect)
      return;

    const select = onSelect.bind(null, match, '');

    switch (mode) {
      case Mode.ARTIST:
        return select(findFilters({
          artist: query
        }));

      case Mode.GENRE:
        return select(findFilters({
          genre: query
        }));

      case Mode.MOOD:
        return select(findFilters({
          mood: query
        }));

      case Mode.THEME:
        return select(findFilters({
          theme: query
        }));
    }
  };

  notifyParentMode = () => {
    if (this.props.onMode)
      this.props.onMode(this.state.mode);
  };

  notifyParentLayout = () => {
    if (this.props.onLayout)
      this.props.onLayout(this.state.layout);
  };

  onFocus = () => {
    this.setState({suggestions: true});

    if (this.state.query && this.$input)
      this.$input.setSelectionRange(0, this.state.query.length);
  };

  onBlur = () => {
    this.setState({suggestions: false})
  };

  updateQuery = (event: React.FormEvent<HTMLInputElement>) => {
    if (!this._mounted || this.state.scrolling)
      return null;

    const {value} = event.currentTarget as HTMLInputElement;

    this.setState({
      suggestions: true,
      query: value
    });
  };

  updateMode = (event: React.FormEvent<HTMLSelectElement>) => this._mounted && this.setState({
    query: '',
    mode: event.currentTarget.value as Mode,
    suggestions: true,
  }, () => {
    this.notifyParentMode();
    this.focusInput();
  });

  updateLayout = (event: React.FormEvent<HTMLSelectElement>) => this._mounted && this.setState({
    layout: parseInt(event.currentTarget.value),
  }, this.notifyParentLayout);

  focusInput = () => {
    if (!this._mounted || !this.$input)
      return;

    this.$input.focus();
  };

  blurInput = () => {
    if (!this._mounted || !this.$input)
      return;

    this.$input.blur();
  };

  getMatchByIndex = (index: number) => {
    return maybe<Match | null>(
      () => this.state.cache![index] || null,
      null
    )
  };

  handleSuggestionSelect = (matches: HeroSearchInputSuggestion[], index: number) => {
    const match = matches[index];
    this.setState({cache: matches});
    if (match) {
      this.selectFromMatch(match);
    };
  };

  handleSuggestionHighlight = (matches: HeroSearchInputSuggestion[], index: number) => {
    const match = matches[index];

    if (match) return this.setState({
      cache: matches,
      activeIndex: index,
      scrolling: true,
      scrollValue: match.name
    });

    this.setState({
      activeIndex: -1,
      scrolling: false,
      scrollValue: ''
    });
  };

  handleSuggestions = (matches: HeroSearchInputSuggestion[]) => {
    this.setState({
      cache: matches
    })
  };

  componentDidMount() {
    this._mounted = true;
    this._placeholderIterator = setInterval(this.iteratePlaceholder, 3000);
    this.focusInput();
  }

  componentWillUnmount() {
    this._mounted = false;
    clearInterval(this._placeholderIterator);
  }

  iteratePlaceholder = () => {
    if (!this._mounted)
      return;

    const {placeholderIndex, cache} = this.state;
    const nextIndex = cache && cache[placeholderIndex + 1]
      ? placeholderIndex + 1
      : 0;

    this.setState({
      placeholderIndex: nextIndex
    });
  };

  static SEARCH_DEBOUNCE = 400;

  static getStateFromFilter = (filters: Partial<FindFiltersInput>): Partial<State> => {
    switch (true) {
      case (!!filters.artist):
        return {
          mode: Mode.ARTIST,
          query: filters.artist!
        };

      case (!!filters.genre):
        return {
          mode: Mode.GENRE,
          query: filters.genre!
        };

      case (!!filters.mood):
        return {
          mode: Mode.MOOD,
          query: filters.mood!
        };

      case (!!filters.theme):
        return {
          mode: Mode.THEME,
          query: filters.theme!
        };

      default:
        return {};
    }
  };

  static styles = {
    container: style(
      {
        ...vertical,
        maxHeight: viewHeight(50),
        width: percent(100),
        borderRadius: rem(0.125),
        overflow: 'hidden',
      },
      mediaMobileOnly({}),
      mediaTablet({
        maxWidth: `calc(100vw - 5.75rem)`,
      })
    ),
    inputs: style({
      ...horizontal,
      flexShrink: 0,
      height: rem(4),
      position: 'relative'
    }),
    input: style({
      '-webkit-appearance': 'none',
      '-moz-appearance': 'none',
      fontFamily: fontBody,
      background: 'transparent',
      borderRadius: 0,
      border: 0,
      color: colorWhite.fade(0.75).toString(),
      borderBottom: `0.125rem solid ${colorWhite.fade(0.75).toString()}`,
      padding: '0 0.5rem',
      margin: 0,
      height: rem(4),
      lineHeight: em(2),
      fontSize: rem(1.25),
      fontWeight: 500,
      willChange: 'border-color, color, padding-left',
      transition: 'all 0.125s linear',
      $nest: {
        '&&&&::placeholder': {
          color: colorWhite.fade(0.75).toString(),
        },
        '&:focus': {
          color: colorWhite.toString(),
          outline: 'none',
          borderBottom: `0.125rem solid ${colorWhite.toString()}`,
        },
        '&:focus + select': {
          color: colorWhite.toString(),
          borderBottom: `0.125rem solid ${colorWhite.toString()}`,
        },
        '&:focus ~ svg path': {
          fill: colorWhite.toString(),
        },
      }
    }),
    inputActive: style({
      color: important(colorWhite.toString()),
      borderBottom: important(`0.125rem solid ${colorWhite.toString()}`),
      $nest: {
        '& svg path': {
          fill: colorWhite.toString(),
        }
      }
    }),
    inputSelect: style({
      direction: 'rtl',
      paddingRight: rem(1.5),
      $nest: {
        '&:focus': {
          borderBottom: important(`0.125rem solid ${colorWhite.fade(0.75).toString()}`),
        },
      }
    }),
    inputQuery: style({
      flex: 1,
    }),
    inputQueryResetVisible: style({
      paddingLeft: rem(3),
    }),
    inputQueryResetHidden: style({
      paddingLeft: rem(0.5),
    }),
    arrow: style({
      position: important('absolute'),
      top: rem(1.625),
      right: rem(0.5),
      transform: 'rotate(90deg)',
      $nest: {
        '& path': {
          transition: 'fill 0.125s ease-in-out',
          stroke: 'none',
          fill: colorWhite.fade(0.75).toString(),
        }
      }
    }),
    reset: style({
      ...centerCenter,
      position: 'absolute',
      width: rem(2.5),
      top: 0,
      left: 0,
      bottom: rem(0.125),
      willChange: 'opacity, transform',
      transition: 'all 0.125s ease-in-out',
    }),
    resetHidden: style({
      opacity: 0,
      transform: 'translate3d(-3rem,0,0,0)'
    }),
    resetVisible: style({
      opacity: 1,
      transform: 'translate3d(0,0,0,0)'
    })
  }
}